import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getDashboardStats, listPwaProjects, me, type Permission } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { canSeeNav } from "../lib/navigation";
import { useRights } from "../lib/useRights";
import { QueryState } from "../components/QueryState";

/**
 * Dashboard d'accueil (Sprint W).
 *
 * Affiche un snapshot quantitatif du contenu dans le scope actif (Client +
 * Etab eventuel) : POI, parcours, medias, expos, collections, projets PWA
 * publies. Servi par GET /admin/stats.
 *
 * Listings raccourcis : les 5 derniers PwaProjects pour voir l'etat
 * brouillon/publie en un coup d'oeil.
 */
/** Raccourcis du tableau de bord : n'apparaissent qu'avec le droit dont le
 *  serveur a besoin pour l'action (créer, importer, envoyer un média =
 *  content.write) — V1.51 : droits de la Gestion des droits. */
const SHORTCUTS: { title: string; hint: string; href: string; perm?: Permission }[] = [
  { title: "Importer en masse", hint: "Excel, CSV ou assistant", href: "/import", perm: "content.write" },
  { title: "Nouveau POI", hint: "Créer une œuvre", href: "/pois", perm: "content.write" },
  { title: "Bibliothèque médias", hint: "Envoyer images et audios", href: "/media", perm: "content.write" },
  { title: "Nouvelle application PWA", hint: "Composer Collections + Expos", href: "/pwa-projects", perm: "content.write" },
  // V1.50 : le comptoir y trouve le diagnostic et « Renouveler l'accès ».
  { title: "Diagnostic d'un billet", hint: "Vérifier ou renouveler un code", href: "/partners", perm: "billing.manage" },
  { title: "Reporting billetterie", hint: "Ventes, annulations, redevance", href: "/reporting/billetterie", perm: "reporting.finance" },
  { title: "Reporting visiteurs", hint: "Usage des PWA", href: "/reporting/visiteurs", perm: "analytics.read" },
];

function Shortcuts({ perms }: { perms: ReadonlySet<Permission> }) {
  return (
    <section className="mb-8">
      <h2 className="text-xs uppercase tracking-wider text-[color:var(--color-alto-muted)] mb-3">
        Raccourcis
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {SHORTCUTS.filter((sc) => canSeeNav(sc, perms)).map((sc) => (
          <ShortcutCard key={sc.title} title={sc.title} hint={sc.hint} href={sc.href} />
        ))}
      </div>
    </section>
  );
}

export function DashboardPage() {
  // V1.50 : sans le droit de voir les contenus (rôle « Commercial » par
  // défaut), ni leurs statistiques ni les applications : seulement les raccourcis.
  const meQuery = useQuery({ queryKey: ["admin", "me"], queryFn: me });
  const role = meQuery.data?.role;
  const { perms, canReadContent: readsContent } = useRights();
  const statsQuery = useQuery({
    queryKey: ["admin", "stats"],
    queryFn: getDashboardStats,
    enabled: readsContent,
  });
  const projectsQuery = useQuery({
    queryKey: ["admin", "pwa-projects"],
    queryFn: listPwaProjects,
    enabled: readsContent,
  });

  return (
    <div className="p-4 md:p-8 max-w-6xl">
      <PageHeader
        overline="Tableau de bord"
        title={
          meQuery.data
            ? `Bonjour ${meQuery.data.displayName || meQuery.data.email.split("@")[0]}`
            : "Tableau de bord"
        }
        hint={
          readsContent || !role
            ? "Vue d'ensemble du contenu dans le scope actif (Client + Établissement)."
            : "Raccourcis vers la billetterie et le reporting."
        }
      />

      {role && !readsContent && <Shortcuts perms={perms} />}

      <QueryState isLoading={statsQuery.isLoading} error={statsQuery.error}>
        {statsQuery.data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-8">
              <StatCard
                title="POI"
                value={statsQuery.data.pois.total}
                sub={`${statsQuery.data.pois.active} publié${statsQuery.data.pois.active > 1 ? "s" : ""}`}
                href="/pois"
              />
              <StatCard
                title="Parcours"
                value={statsQuery.data.parcours.total}
                sub={`${statsQuery.data.parcours.active} actif${statsQuery.data.parcours.active > 1 ? "s" : ""}`}
                href="/parcours"
              />
              <StatCard
                title="Collections permanentes"
                value={statsQuery.data.collections.total}
                href="/collections"
              />
              <StatCard
                title="Expositions temporaires"
                value={statsQuery.data.exhibitions.total}
                href="/exhibitions"
              />
              <StatCard
                title="Médias"
                value={statsQuery.data.medias.total}
                sub="bibliothèque"
                href="/media"
              />
              <StatCard
                title="Applications PWA"
                value={
                  statsQuery.data.pwaProjects.live + statsQuery.data.pwaProjects.draft
                }
                sub={`${statsQuery.data.pwaProjects.live} en prod · ${statsQuery.data.pwaProjects.draft} brouillon`}
                href="/pwa-projects"
                highlight
              />
            </div>

            {/* V1.13 (24/09/2026) : la billetterie et l'analytics visiteurs ont
                quitté le tableau de bord pour le menu Reporting — ici, le seul
                état du back-office. */}
            {/* Sprint Z (item 3) : Raccourcis remonte avant Applications PWA
              recentes — l'action prime sur l'info. V1.25 : filtres par role,
              comme le menu (lib/navigation.ts). */}
            <Shortcuts perms={perms} />

            <section className="mb-8">
              <div className="flex items-baseline justify-between mb-3">
                <h2 className="text-xs uppercase tracking-wider text-[color:var(--color-alto-muted)]">
                  Applications PWA récentes
                </h2>
                <Link
                  to="/pwa-projects"
                  className="text-xs text-[color:var(--color-alto-accent)] hover:underline"
                >
                  Tout voir →
                </Link>
              </div>
              <QueryState
                isLoading={projectsQuery.isLoading}
                error={projectsQuery.error}
                empty={(projectsQuery.data?.length ?? 0) === 0}
              >
                <div className="overflow-x-auto rounded-xl border border-[color:var(--color-alto-border)] bg-white">
                  <table className="w-full text-sm min-w-[700px]">
                    <thead className="bg-[color:var(--color-alto-bg)] text-left text-[color:var(--color-alto-muted)] text-xs uppercase tracking-wider">
                      <tr>
                        <th className="px-4 py-2 font-medium">Nom</th>
                        <th className="px-4 py-2 font-medium">Slug</th>
                        <th className="px-4 py-2 font-medium">État</th>
                        <th className="px-4 py-2 font-medium">Dernière publication</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[color:var(--color-alto-border)]">
                      {projectsQuery.data?.slice(0, 5).map((p) => (
                        <tr
                          key={p.id}
                          className="hover:bg-[color:var(--color-alto-bg)] cursor-pointer"
                        >
                          <td className="px-4 py-2 font-medium">
                            <Link to={`/pwa-projects/${p.id}`} className="hover:underline">
                              {p.displayName}
                            </Link>
                          </td>
                          <td className="px-4 py-2 font-mono text-xs text-[color:var(--color-alto-muted)]">
                            {p.slug}
                          </td>
                          <td className="px-4 py-2">
                            {p.state === "LIVE" ? (
                              <span className="inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-md text-emerald-700 bg-emerald-50 border border-emerald-200">
                                <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                LIVE
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-md text-amber-700 bg-amber-50 border border-amber-200">
                                <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" />
                                DRAFT
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-xs text-[color:var(--color-alto-muted)]">
                            {p.publishedAt
                              ? new Date(p.publishedAt).toLocaleDateString("fr-FR", {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })
                              : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </QueryState>
            </section>
          </>
        )}
      </QueryState>
    </div>
  );
}

interface StatCardProps {
  title: string;
  value: number;
  sub?: string;
  href: string;
  highlight?: boolean;
}

function StatCard({ title, value, sub, href, highlight }: StatCardProps) {
  return (
    <Link
      to={href}
      className={
        "block rounded-xl border p-4 transition-colors " +
        (highlight
          ? "border-[color:var(--color-alto-accent)]/30 bg-[color:var(--color-alto-accent)]/5 hover:bg-[color:var(--color-alto-accent)]/10"
          : "border-[color:var(--color-alto-border)] bg-white hover:bg-[color:var(--color-alto-bg)]")
      }
    >
      <p className="text-xs uppercase tracking-wider text-[color:var(--color-alto-muted)]">
        {title}
      </p>
      <p className="text-3xl font-semibold mt-2 tabular-nums">{value}</p>
      {sub && (
        <p className="text-xs text-[color:var(--color-alto-muted)] mt-1">{sub}</p>
      )}
    </Link>
  );
}


function ShortcutCard({
  title,
  hint,
  href,
}: {
  title: string;
  hint: string;
  href: string;
}) {
  return (
    <Link
      to={href}
      className="block rounded-xl border border-[color:var(--color-alto-border)] bg-white p-4 hover:bg-[color:var(--color-alto-bg)] hover:border-[color:var(--color-alto-ink)] transition-colors"
    >
      <p className="font-medium text-sm">{title}</p>
      <p className="text-xs text-[color:var(--color-alto-muted)] mt-0.5">{hint}</p>
    </Link>
  );
}
